import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = <>
    <h3 className="text-2xl font-serif text-slate-50 mb-6" data-id="element-489">Privacy Policy</h3>
    <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-490">
      <p data-id="element-491">
        <strong className="text-slate-100" data-id="element-492">Last Updated:</strong> January 2025
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-493">
        Information We Collect
      </h4>
      <p data-id="element-494">
        When you purchase "The Cortisol Switch," we collect only the information
        necessary to process your order: your name, email address, and payment
        details. Payment processing is handled securely by our third-party
        payment processor — we never store your full credit card information on
        our servers.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-495">
        How We Use Your Information
      </h4>
      <p data-id="element-496">
        Your information is used solely to deliver your digital product, send
        your purchase confirmation, and provide customer support if needed. We
        do not sell, rent, or share your personal information with third parties
        for marketing purposes.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-497">Cookies</h4>
      <p data-id="element-498">
        Our website may use essential cookies to ensure proper functionality and
        analytics cookies to understand how visitors interact with our page. You
        can disable cookies in your browser settings at any time.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-499">Data Security</h4>
      <p data-id="element-500">
        We implement industry-standard security measures including SSL
        encryption to protect your personal information during transmission and
        storage.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-501">Your Rights</h4>
      <p data-id="element-502">
        You have the right to access, correct, or delete your personal data at
        any time. You may also unsubscribe from any email communications using
        the link provided in each email.
      </p>
    </div>
  </>;
const termsContent = <>
    <h3 className="text-2xl font-serif text-slate-50 mb-6" data-id="element-503">
      Terms & Conditions
    </h3>
    <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-504">
      <p data-id="element-505">
        <strong className="text-slate-100" data-id="element-506">Last Updated:</strong> January 2025
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-507">
        Product Description
      </h4>
      <p data-id="element-508">
        "The Cortisol Switch" is a digital educational manual provided in PDF
        format. It is designed for informational and educational purposes only
        and is not intended to replace professional medical advice, diagnosis,
        or treatment.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-509">
        Medical Disclaimer
      </h4>
      <p data-id="element-510">
        The content within this manual is based on research and general wellness
        principles. Always consult with a qualified healthcare professional
        before making changes to your diet, exercise routine, or health regimen.
        Individual results may vary.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-511">
        Digital Delivery
      </h4>
      <p data-id="element-512">
        Upon successful payment, you will receive immediate access to download
        the digital product. Due to the nature of digital products, all sales
        are final once the download link has been accessed.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-513">
        Intellectual Property
      </h4>
      <p data-id="element-514">
        All content within "The Cortisol Switch" is protected by copyright. You
        may not reproduce, distribute, or resell any part of this product
        without prior written consent.
      </p>

      <h4 className="text-lg text-slate-100 font-medium pt-2" data-id="element-515">
        Limitation of Liability
      </h4>
      <p data-id="element-516">
        We provide this product "as is" and make no guarantees regarding
        specific health outcomes. By purchasing, you acknowledge that results
        depend on individual circumstances and consistent application of the
        methods described.
      </p>
    </div>
  </>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 bg-slate-900 border-t border-slate-800" data-id="element-517">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-518">
          <p className="text-slate-500 text-sm" data-id="element-519">
            © {new Date().getFullYear()} The Cortisol Switch. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-520">
            <button onClick={() => setActiveModal('privacy')} className="text-slate-500 text-sm hover:text-slate-300 transition-colors" data-id="element-521">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-slate-500 text-sm hover:text-slate-300 transition-colors" data-id="element-522">
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-523">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-524">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-525">
              <div className="flex items-center justify-between mb-6" data-id="element-526">
                <div className="w-8" data-id="element-527" />
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-slate-200" data-id="element-528">
                  <X className="w-5 h-5" data-id="element-529" />
                </button>
              </div>
              {activeModal === 'privacy' ? privacyContent : termsContent}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}